import type { Domain } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import { Button, Text, Alert } from "@amzn/storm-ui";
import { isEmpty, isUndefined } from "lodash";
import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useStore } from "zustand";

import { TELEMETRY_ID } from "../../constants/telemetry";
import { useTemplateAccessValidation } from "../../hooks/misc/useTemplateAccessValidation";
import { useSfAccountsAction } from "../../hooks/salesforce/useSfAccountsAction";
import { reportIssueStore } from "../../store/reportIssue";
import { TopNavBarTabs } from "../../types/navigation";
import { isError } from "../../utils/api.utils";
import { logWithTakt } from "../../utils/telemetry.utils";
import {
    DeepLinkContainer,
    DeepLinkHeader,
    DeepLinkHeaderText,
    DeepLinkTemplateInfo,
    DeepLinkTemplateDescription,
    DeepLinkFooter,
    DeepLinkStepText,
    DeepLinkAccessDeniedContainer,
    DeepLinkNoAccountsSteps,
} from "../common/styled.components";

import AccountDropdown from "./AccountDropdown";
import { AccountPickerError } from "./AccountPickerError";

export interface DeepLinkTemplateAccountPickerProps {
    domain: Domain;
    templateId: string;
    onAccountSelect: (accountId: string) => void;
    onCancel: () => void;
    onClose: () => void;
    onAccessDenied?: (accountId: string) => void;
}

export const DeepLinkTemplateAccountPicker: React.FunctionComponent<
    DeepLinkTemplateAccountPickerProps
> = ({
    domain,
    templateId,
    onAccountSelect,
    onCancel,
    onClose,
    onAccessDenied,
}) => {
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
        null,
    );
    const [isValidatingAccess, setIsValidatingAccess] = useState(false);
    const [accessError, setAccessError] = useState<string | null>(null);
    const [showAccessDeniedMessage, setShowAccessDeniedMessage] =
        useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    const [accountsState, accountsAction] = useSfAccountsAction();
    const { validateTemplateAccess } = useTemplateAccessValidation();

    // Report issue store for CTI-based issue reporting
    const [setReportIssueContextData] = useStore(reportIssueStore, (state) => [
        state.setReportIssueContextData,
    ]);

    // Get template information
    const template = domain.templates?.find((t) => t.id === templateId);
    const templateName = template?.name;
    const templateCti = domain.cti;

    useEffect(() => {
        const fetchAccounts = async () => {
            if (isUndefined(accountsState.result)) {
                await accountsAction.execute().catch(() => {});
            }
        };

        void fetchAccounts();
    }, [accountsState.result]);

    useEffect(() => {
        if (!isUndefined(accountsState.result)) {
            setHasFetched(true);
        }
    }, [accountsState.result, accountsState.status, accountsState.error]);

    useEffect(() => {
        logWithTakt(TELEMETRY_ID.TEMPLATE_ACCOUNT_PICKER_OPEN, {
            templateId,
            templateName,
        });
    }, [templateId, templateName]);

    const handleAccountSelect = useCallback(
        (accountId: string) => {
            if (!accountId) {
                onCancel();
                return;
            }
            setSelectedAccountId(accountId);
            setAccessError(null);
            setShowAccessDeniedMessage(false);

            logWithTakt(TELEMETRY_ID.TEMPLATE_ACCOUNT_PICKER_SELECTION, {
                selectedAccountId: accountId,
                templateId,
            });
        },
        [templateId, onCancel],
    );

    const handleUseAccount = useCallback(async () => {
        if (!selectedAccountId) return;

        setIsValidatingAccess(true);
        setAccessError(null);

        const validationResult = await validateTemplateAccess(
            domain,
            templateId,
            selectedAccountId,
        );

        if (validationResult.hasAccess) {
            // User has access, proceed
            const updatedQueryParams = new URLSearchParams(location.search);
            updatedQueryParams.set("sfId", selectedAccountId);
            const newUrl = `${location.pathname}?${updatedQueryParams.toString()}`;

            navigate(newUrl);

            logWithTakt(TELEMETRY_ID.TEMPLATE_ACCOUNT_SWITCH_SUCCESS, {
                newAccountId: selectedAccountId,
                templateId,
            });
            window.location.reload();
            onAccountSelect(selectedAccountId);
            onClose();
        } else {
            // User doesn't have access, show access denied message
            setShowAccessDeniedMessage(true);
            setAccessError(validationResult.reason ?? "Access denied");

            logWithTakt(TELEMETRY_ID.TEMPLATE_ACCOUNT_ACCESS_DENIED, {
                accountId: selectedAccountId,
                templateId,
                reason: validationResult.reason,
            });

            if (onAccessDenied) {
                onAccessDenied(selectedAccountId);
            }
        }

        setIsValidatingAccess(false);
    }, [
        selectedAccountId,
        templateId,
        domain,
        validateTemplateAccess,
        location,
        navigate,
        onAccountSelect,
        onClose,
        onAccessDenied,
    ]);

    const handleCancel = useCallback(() => {
        // Navigate back to home when cancel is clicked
        navigate("/");
        onCancel();
        logWithTakt(TELEMETRY_ID.TEMPLATE_ACCOUNT_PICKER_CANCEL, {
            templateId,
        });
    }, [navigate, onCancel, templateId]);

    // Set context data for the global report issue button when access is denied
    useEffect(() => {
        if (showAccessDeniedMessage) {
            const contextData = {
                activeTab: TopNavBarTabs.ALL_INSIGHTS,
                domainIdPathParam: domain.id,
                templateId,
                templateName,
                accessDeniedScenario: true,
                selectedAccountId,
                templateCti,
            };
            setReportIssueContextData(contextData);
        }
    }, [
        showAccessDeniedMessage,
        domain.id,
        templateId,
        templateName,
        selectedAccountId,
        templateCti,
        setReportIssueContextData,
    ]);

    const renderAccessDeniedMessage = () => {
        const handleViewMainPage = () => {
            const mainPageUrl = `/?sfId=${selectedAccountId}`;
            navigate(mainPageUrl);
        };

        return (
            <Alert
                variant="warning"
                header="Access Required"
                data-testid="template-access-denied-alert"
            >
                <Text>
                    The selected Salesforce account doesn't have access to this
                    packaged insight template. Please select a different
                    account. Otherwise, view content scoped to this salesforce
                    account on{" "}
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleViewMainPage();
                        }}
                    >
                        IHub
                    </a>
                    .
                </Text>
            </Alert>
        );
    };

    const renderNoAccountsMessage = () => {
        return (
            <Alert
                variant="warning"
                header="No Salesforce Accounts Available"
                data-testid="no-accounts-available-alert"
            >
                <Text>You are not associated to any Salesforce accounts.</Text>
                <DeepLinkNoAccountsSteps>
                    <Text>
                        <strong>Next steps:</strong>
                    </Text>
                    <DeepLinkStepText>
                        1. Follow this guide to request temporary ATC access:{" "}
                        <a
                            href="https://w.amazon.com/bin/view/CP2/InsightsHub/ShortTermATCAccessForSFAccounts/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Short Term ATC Access Guide
                        </a>
                    </DeepLinkStepText>
                    <DeepLinkStepText>
                        2. Or visit{" "}
                        <a
                            href="/"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate("/");
                            }}
                        >
                            Insights Hub
                        </a>{" "}
                        to view account agnostic content
                    </DeepLinkStepText>
                </DeepLinkNoAccountsSteps>
            </Alert>
        );
    };

    const renderFooter = useCallback(() => {
        const isButtonDisabled =
            isEmpty(selectedAccountId) ?? isValidatingAccess;

        return (
            <DeepLinkFooter>
                <Button onClick={handleCancel} data-testid="cancel-button">
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => void handleUseAccount()}
                    disabled={isButtonDisabled}
                    loading={isValidatingAccess}
                    data-testid="use-account-button"
                >
                    {isValidatingAccess
                        ? "Validating Access..."
                        : "Use Account"}
                </Button>
            </DeepLinkFooter>
        );
    }, [selectedAccountId, isValidatingAccess, handleCancel, handleUseAccount]);

    if (!hasFetched) {
        return null;
    }

    return (
        <DeepLinkContainer
            id="deep-link-template-account-picker"
            data-testid="deep-link-template-account-picker"
        >
            <DeepLinkHeader>
                <DeepLinkHeaderText>
                    Select Account for Packaged Insight Template
                </DeepLinkHeaderText>
            </DeepLinkHeader>
            {isError(accountsState.status) ? (
                <AccountPickerError />
            ) : (
                <>
                    <DeepLinkTemplateInfo>
                        <Text>
                            <strong>{templateName}</strong> is scoped to a
                            Salesforce advertiser. Please select a Salesforce
                            account to access this template.
                        </Text>
                        {template?.description && (
                            <DeepLinkTemplateDescription>
                                <Text>{template.description}</Text>
                            </DeepLinkTemplateDescription>
                        )}
                    </DeepLinkTemplateInfo>

                    {/* Show no accounts message if no accounts are available */}
                    {hasFetched && isEmpty(accountsState.result) ? (
                        renderNoAccountsMessage()
                    ) : (
                        <>
                            <AccountDropdown
                                selectedAccountId={selectedAccountId}
                                onAccountSelect={handleAccountSelect}
                                onCancel={handleCancel}
                                showNoAccount={false}
                            />

                            {accessError && !showAccessDeniedMessage && (
                                <Alert
                                    variant="error"
                                    header="Validation Error"
                                    data-testid="access-validation-error"
                                >
                                    <Text>{accessError}</Text>
                                </Alert>
                            )}

                            {showAccessDeniedMessage && (
                                <DeepLinkAccessDeniedContainer>
                                    {renderAccessDeniedMessage()}
                                </DeepLinkAccessDeniedContainer>
                            )}

                            {renderFooter()}
                        </>
                    )}
                </>
            )}
        </DeepLinkContainer>
    );
};
